import { createSlice, PayloadAction } from '@reduxjs/toolkit';
import type { UserActionType } from '@wix/ambassador-funnel-chatbot-v1-session/types';
import isEmpty from 'lodash/isEmpty';
import isEqual from 'lodash/isEqual';

import { MESSAGES } from '../../../constants';
import { isNumber } from '../../../util/guards';
import { createChatMessage, isLiveChatMode } from '../../messageChannel/chatCreators';
import { sendMessageReady } from '../../messageChannel/postNewMessages';
import { renderModel } from '../../util/renderModel';
import { createBotTyping } from '../messageCreators';
import { loadArticlePreview } from '../thunks/articlePreview.thunk';
import { loadChatbotStartChat, StartChatParams } from '../thunks/chatbotStartChat.thunk';
import { changeMessage, concatItems, disableChangeFeature, omit } from './chatTransforms';
import { headerActions } from './header';
import {
  ChatScrollToPositions,
  ErrorStatus,
  IChatState,
  IUndelivered,
  IUserAction,
  ResetReason,
  RestartChatParameters,
  TItem,
} from './types';

const initialState: IChatState = {
  isLocked: false,
  isLiveChat: false,
  isMuted: false,
  isConnectingAgent: false,
  isDocumentVisible: undefined,
  conversationId: '',
  items: [],
  undelivered: {},
  unreadCount: 0,
  scrollTo: undefined,
  isFirstPriorityUser: false,
  isAiEnabled: false,
  itemsIdsOnLastPageLoad: [],
};

const isCopilot = renderModel.isCopilot();

export const chatSlice = createSlice({
  name: 'chat',
  initialState,
  reducers: {
    addItem: (state, action: PayloadAction<TItem>) => {
      state.isReset = false;
      state.items = concatItems(state.items, action.payload);
    },
    changeMessage: (state) => {
      state.isLocked = true;
      state.items = changeMessage(state.items);
    },
    reset: (state, action: PayloadAction<ResetReason>) => {
      state.isLocked = false;
      state.isConnectingAgent = false;
      state.isReset = true;
      state.resetReason = action.payload;
      if (state.items.length > 0 || action.payload === ResetReason.StartOver) {
        state.items = [createBotTyping({ rm: true })];
      }
    },
    toggleMute: (state, action: PayloadAction<boolean>) => {
      state.isMuted = action.payload;
    },
    toggleLock: (state, action: PayloadAction<boolean>) => {
      state.isLocked = action.payload;
    },
    addUndelivered: (
      state,
      action: PayloadAction<{
        readonly [key: string]: IUserAction | UserActionType;
      }>,
    ) => {
      state.items = state.items.filter((item) => item.type !== MESSAGES.BOT_TYPING);
      state.isLocked = false;
      state.undelivered = {
        ...state.undelivered,
        ...action.payload,
      } as IUndelivered;
    },
    removeUndelivered: (state, action: PayloadAction<string>) => {
      state.undelivered = omit(state.undelivered, action.payload) as IUndelivered;
    },
    updateSession: (state, action: PayloadAction<Pick<IChatState, 'conversationId'>>) => {
      state.conversationId = action.payload.conversationId;
    },
    increaseUnreadCount: (state) => {
      const { unreadCount } = state;
      state.unreadCount = isNumber(unreadCount) ? unreadCount + 1 : 1;
    },
    updateUnreadCount: (state, action: PayloadAction<number>) => {
      state.unreadCount = action.payload;
    },
    resetUnreadCount: (state) => {
      state.unreadCount = 0;
    },
    scrollTo: (state, action: PayloadAction<ChatScrollToPositions>) => {
      state.scrollTo = { position: action.payload };
    },
    changeRestartChatParameters: (state, action: PayloadAction<RestartChatParameters>) => {
      state.restartChatParameters = action.payload;
    },
    startChatFromNode: (state, action: PayloadAction<string>) => {
      state.startingNodeId = action.payload;
    },
    // if chatbot is open it will request start chat immediately. If no - it will request start chat when chatbot is open
    setReloadStartChat: (state, action: PayloadAction<boolean>) => {
      state.reloadStartChat = action.payload;
    },
    setStartChatParameters: (state, action: PayloadAction<StartChatParams | undefined>) => {
      // if params are not defined and new params are empty object
      if (!state.startChatParameters && isEmpty(action.payload)) {
        return;
      }
      // do not need to request a start chat of params(dealer offer params) are the same
      if (isEqual(state.startChatParameters, action.payload)) {
        return;
      }
      state.startChatParameters = action.payload;
      state.reloadStartChat = true;
    },
    setIsFirstPriorityUser: (state, action: PayloadAction<boolean>) => {
      state.isFirstPriorityUser = action.payload;
    },
    cleanUpBotTyping: (state) => {
      if (state.items.length === 1 && state.items[0].type === MESSAGES.BOT_TYPING) {
        state.items = [];
      }
    },
    startConnectingAgent: (state) => {
      state.isConnectingAgent = true;
    },
    setIsDocumentVisible: (state, action: PayloadAction<boolean>) => {
      state.isDocumentVisible = action.payload;
    },
  },
  extraReducers: (builder) => {
    builder.addCase(headerActions.changeChatMode, (state, action) => {
      state.isLocked = false;
      state.isLiveChat = action.payload;
      state.isConnectingAgent = false;
      // When we toggle to live chat then we need to disable all change buttons in the user messages
      state.items = action.payload ? disableChangeFeature([...state.items]) : state.items;
    });
    builder.addCase(loadChatbotStartChat.pending, (state) => {
      state.startChatLoading = true;
      state.startChatError = undefined;
    });

    builder.addCase(loadChatbotStartChat.fulfilled, (state, action) => {
      const { startChatData, unreadMessagesCount, messages } = action.payload;
      const isLiveChat = isLiveChatMode(startChatData?.mode);
      const items: TItem[] = messages?.reduce((acc: TItem[], message: unknown) => {
        const item = createChatMessage(message);
        return item ? concatItems(acc, item) : acc;
      }, []);
      if (isCopilot) {
        sendMessageReady();
      }

      state.isLiveChat = isLiveChat;
      state.isFirstPriorityUser = startChatData?.priorityOneUser;
      state.isAiEnabled = startChatData?.aiEnabled || false;
      state.conversationId = startChatData?.conversationId;
      state.items = items;
      state.itemsIdsOnLastPageLoad = items.map(({ id }) => id);
      state.unreadCount = unreadMessagesCount || 0;
      state.startChatLoading = false;
      state.startChatError = undefined;
      state.reloadStartChat = false;
      state.articlePreviews = {}; // remove fetched articles for previous conversation
    });

    builder.addCase(loadChatbotStartChat.rejected, (state, action) => {
      state.startChatLoading = false;
      state.startChatError = action.error.message;
      if (isCopilot) {
        sendMessageReady();
      }
    });

    builder.addCase(loadArticlePreview.pending, (state, action) => {
      const articleId = action.meta.arg.id;
      state.articlePreviews = {
        ...state.articlePreviews,
        [articleId]: {
          article: null,
          isLoading: true,
          error: null,
        },
      };
    });

    builder.addCase(loadArticlePreview.fulfilled, (state, action) => {
      const articleId = action.meta.arg.id;
      state.articlePreviews = {
        ...state.articlePreviews,
        [articleId]: {
          article: action.payload,
          isLoading: false,
          error: null,
        },
      };
    });

    builder.addCase(loadArticlePreview.rejected, (state, action) => {
      const articleId = action.meta.arg.id;
      state.articlePreviews = {
        ...state.articlePreviews,
        [articleId]: {
          article: null,
          isLoading: false,
          error: action.payload || ErrorStatus.ServerError,
        },
      };
    });
  },
});

export const chatActions = chatSlice.actions;
